import { useMemo } from 'react';
import { FiltroSeccion, FiltrosPopover } from '@/components/filtros/filtros-popover';
import { GrupoChips } from '@/components/filtros/grupo-chips';
import type { ConteoFacetas, OpcionFiltro } from '@/components/filtros/tipos';
import type { ObraCard } from '@/types/api';
import {
  DIM_CONTROL_RDT,
  DIM_ESTADO_OBRA,
  DIM_ESTADO_REDES,
  ENC_NOMBRES,
  ESTADOS_REDES_EN_CURSO,
  ESTADOS_REDES_SOLO_ARCHIVO,
  ESTADO_OBRA_ORDEN,
  SIN_CONTROL_RDT,
  SIN_ESTADO_OBRA,
  SIN_ESTADO_REDES,
  TODO_EL_EQUIPO_OBRAS,
  UNIDAD_OBRAS,
  type FiltrosObras,
} from './obras-filtros-tipos';

// Popover «Filtros de obras» (R4a — Home con Filtros, plan_migracion_reui.md
// §3, sección «R4a — Home con Filtros»; síntesis del panel,
// context/home_filtros_panel/sintesis.md §2 «Popover»; BACKLOG 25/9,
// decisiones 1-5 de Juan). Mismo patrón que
// components/avivador/filtros-avivador-popover.tsx: compone las piezas
// COMPARTIDAS de components/filtros/ (trigger, cabecera, pie, foco y alto
// máximo viven en FiltrosPopover) con las 3 secciones de la home — universo →
// persona → atributo, el mismo orden que el Avivador. No calcula el filtrado
// ni el conteo total: los recibe ya armados de pages/obras.tsx
// (obras-filtros.ts, R4a-A).
//
// Las opciones de cada sección SÍ se arman acá (es la capa de UI, no el
// contrato de filtrado): universo fijo para Estado de redes y Estado de obra
// (con los desconocidos de los datos al final, alfabético — mismo criterio
// que el `computeEstadoOptions`/`computeEncargadaOptions` que reemplaza) y los
// códigos de Control RDT presentes en los datos (conocidos de ENC_NOMBRES
// primero). Las pseudo-opciones «(Sin …)» solo se ofrecen con conteo > 0
// (decisión 3 de Juan: No Aplica/Archivado y Sin Control RDT, solo con
// `archivadas` prendido).

interface FiltrosObrasPopoverProps {
  obras: readonly ObraCard[];
  filtros: FiltrosObras;
  facetas: ConteoFacetas;
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  nActivos: number;
  nBusqueda: number;
  puedeLimpiar: boolean;
  onLimpiar: () => void;
  onFiltrosChange: (patch: Partial<FiltrosObras>) => void;
  /** «N obras con estos filtros» del pie: el mismo conteo que ya muestra la
   *  meta-row (con la búsqueda en vivo incluida), no un recálculo propio. */
  totalConEstosFiltros: number;
}

/** Códigos de Control RDT presentes en los datos: los de ENC_NOMBRES en su
 *  orden fijo primero, los desconocidos alfabético después (mismo criterio
 *  que el `computeEncargadaOptions` que esta pieza reemplaza) — un código
 *  nuevo/retipeado que no matchee ENC_NOMBRES no queda invisible. */
function codigosControlRdt(obras: readonly ObraCard[]): string[] {
  const presentes = new Set(obras.map(o => o.encargada).filter((v): v is string => Boolean(v)));
  const conocidos = Object.keys(ENC_NOMBRES).filter(c => presentes.has(c));
  const desconocidos = [...presentes].filter(c => !(c in ENC_NOMBRES)).sort((a, b) => a.localeCompare(b));
  return [...conocidos, ...desconocidos];
}

/** Estados de obra presentes: el orden fijo de ESTADO_OBRA_ORDEN primero, los
 *  desconocidos alfabético después (mismo criterio que `computeEstadoOptions`). */
function estadosObraPresentes(obras: readonly ObraCard[]): string[] {
  const presentes = new Set(obras.map(o => o.estado).filter((v): v is string => Boolean(v)));
  const ordenados = ESTADO_OBRA_ORDEN.filter(e => presentes.has(e));
  const desconocidos = [...presentes].filter(e => !ESTADO_OBRA_ORDEN.includes(e)).sort((a, b) => a.localeCompare(b));
  return [...ordenados, ...desconocidos];
}

const fmt = (n: number) => n.toLocaleString('es-AR');

export function FiltrosObrasPopover({
  obras,
  filtros,
  facetas,
  abierto,
  onAbiertoChange,
  nActivos,
  nBusqueda,
  puedeLimpiar,
  onLimpiar,
  onFiltrosChange,
  totalConEstosFiltros,
}: FiltrosObrasPopoverProps) {
  const opcionesRedes = useMemo<OpcionFiltro[]>(() => {
    const facetasRedes = facetas[DIM_ESTADO_REDES] ?? {};
    const base = [...ESTADOS_REDES_EN_CURSO, ...(filtros.archivadas ? ESTADOS_REDES_SOLO_ARCHIVO : [])];
    const opciones: OpcionFiltro[] = base.map(v => ({ valor: v, etiqueta: v, conteo: facetasRedes[v] ?? 0 }));
    if ((facetasRedes[SIN_ESTADO_REDES] ?? 0) > 0) {
      opciones.push({ valor: SIN_ESTADO_REDES, etiqueta: SIN_ESTADO_REDES, conteo: facetasRedes[SIN_ESTADO_REDES] ?? 0 });
    }
    return opciones;
  }, [facetas, filtros.archivadas]);

  const opcionesControlRdt = useMemo<OpcionFiltro[]>(() => {
    const facetasCtrl = facetas[DIM_CONTROL_RDT] ?? {};
    const opciones: OpcionFiltro[] = [
      { valor: TODO_EL_EQUIPO_OBRAS, etiqueta: 'Todo el equipo', conteo: facetasCtrl[TODO_EL_EQUIPO_OBRAS] ?? 0 },
      ...codigosControlRdt(obras).map(c => ({ valor: c, etiqueta: ENC_NOMBRES[c] ?? c, conteo: facetasCtrl[c] ?? 0 })),
    ];
    if (filtros.archivadas && (facetasCtrl[SIN_CONTROL_RDT] ?? 0) > 0) {
      opciones.push({ valor: SIN_CONTROL_RDT, etiqueta: SIN_CONTROL_RDT, conteo: facetasCtrl[SIN_CONTROL_RDT] ?? 0 });
    }
    return opciones;
  }, [obras, facetas, filtros.archivadas]);

  const opcionesEstadoObra = useMemo<OpcionFiltro[]>(() => {
    const facetasEo = facetas[DIM_ESTADO_OBRA] ?? {};
    const opciones: OpcionFiltro[] = estadosObraPresentes(obras).map(v => ({ valor: v, etiqueta: v, conteo: facetasEo[v] ?? 0 }));
    if ((facetasEo[SIN_ESTADO_OBRA] ?? 0) > 0) {
      opciones.push({ valor: SIN_ESTADO_OBRA, etiqueta: SIN_ESTADO_OBRA, conteo: facetasEo[SIN_ESTADO_OBRA] ?? 0 });
    }
    return opciones;
  }, [obras, facetas]);

  return (
    <FiltrosPopover
      abierto={abierto}
      onAbiertoChange={onAbiertoChange}
      nActivos={nActivos}
      ariaLabel="Filtros de obras"
      puedeLimpiar={puedeLimpiar}
      onLimpiar={onLimpiar}
      nBusqueda={nBusqueda}
      pie={
        <>
          <b className="font-semibold tabular-nums text-foreground">{fmt(totalConEstosFiltros)}</b>{' '}
          {totalConEstosFiltros === 1 ? 'obra' : 'obras'} con estos filtros
        </>
      }
    >
      <FiltroSeccion titulo="Estado de redes">
        <GrupoChips
          modo="multiple"
          opciones={opcionesRedes}
          valor={filtros.estadosRedes}
          onValorChange={estadosRedes => onFiltrosChange({ estadosRedes })}
          unidad={UNIDAD_OBRAS}
        />
      </FiltroSeccion>
      <FiltroSeccion titulo="Control RDT">
        <GrupoChips
          modo="unico"
          opciones={opcionesControlRdt}
          valor={filtros.controlRdt}
          onValorChange={controlRdt => onFiltrosChange({ controlRdt })}
          unidad={UNIDAD_OBRAS}
        />
      </FiltroSeccion>
      <FiltroSeccion titulo="Estado de obra">
        <GrupoChips
          modo="multiple"
          opciones={opcionesEstadoObra}
          valor={filtros.estadosObra}
          onValorChange={estadosObra => onFiltrosChange({ estadosObra })}
          unidad={UNIDAD_OBRAS}
        />
      </FiltroSeccion>
    </FiltrosPopover>
  );
}
